import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Stack,
  IconButton,
  Tooltip as MuiTooltip,
  Fade,
  useTheme,
  alpha,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Assessment,
  Security,
  Refresh,
  Timeline,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart,
  CloudUpload,
  Science,
  Warning,
  CheckCircle,
  Info,
  CalendarToday,
  Analytics as AnalyticsIcon,
  Insights,
  DataUsage,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  ComposedChart,
  Legend,
  Line,
} from 'recharts';
import { motion } from 'framer-motion';
import { apiService } from '../services/apiService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Enhanced color palette for charts
const CHART_COLORS = [
  '#7D1F8E', // Primary purple
  '#EF7504', // Primary orange
  '#D2565A', // Secondary red
  '#B83C78', // Secondary pink
  '#4CAF50', // Success green
  '#2196F3', // Info blue
  '#FF9800', // Warning orange
  '#9C27B0', // Purple variant
  '#00BCD4', // Cyan
  '#795548', // Brown
];

const AnalyticsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [activityDays, setActivityDays] = useState<number>(30);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => apiService.getDashboardStats(),
  });

  const { data: activity, isLoading: activityLoading, error: activityError } = useQuery({
    queryKey: ['userActivity', activityDays],
    queryFn: () => apiService.getUserActivity(activityDays),
  });

  const { data: systemHealth, isLoading: healthLoading } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: () => apiService.getSystemHealth(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: detailedAnalytics, isLoading: detailedLoading } = useQuery({
    queryKey: ['detailedAnalytics'],
    queryFn: () => apiService.getDetailedAnalytics(),
  });

  const { data: performanceMetrics, isLoading: performanceLoading } = useQuery({
    queryKey: ['performanceMetrics', activityDays],
    queryFn: () => apiService.getPerformanceMetrics(activityDays),
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRefresh = async () => {
    setLoading(true);
    // Simulate refresh
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  // Prepare chart data
  const bodyPartData = stats?.body_part_distribution?.map(item => ({
    name: item._id || 'Not Specified',
    value: item.count,
  })) || [];

  const riskData = stats?.risk_distribution?.map(item => ({
    name: item._id || 'Unknown',
    value: item.count,
  })) || [];

  const activityData = React.useMemo(() => {
    if (!activity) return [];

    const uploadsMap = new Map(activity.daily_uploads?.map(item => [item._id, item.uploads]) || []);
    const analysesMap = new Map(activity.daily_analyses?.map(item => [item._id, item.analyses]) || []);

    const allDates = new Set([
      ...(activity.daily_uploads?.map(item => item._id) || []),
      ...(activity.daily_analyses?.map(item => item._id) || []),
    ]);

    return Array.from(allDates)
      .sort()
      .map(date => ({
        date,
        uploads: uploadsMap.get(date) || 0,
        analyses: analysesMap.get(date) || 0,
      }));
  }, [activity]);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (statsError || activityError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            📊 Melanoma Detection Analytics
          </Typography>
          <Alert severity="error">
            Failed to load dashboard data. Please check if the server is running and try again.
          </Alert>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Box>
        {/* Header Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  📊 System Analytics
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Comprehensive insights and performance metrics • Admin Dashboard
                </Typography>
              </Box>

              <MuiTooltip title="Refresh Data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading || statsLoading || activityLoading}
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.2),
                      transform: 'rotate(180deg)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Refresh />
                </IconButton>
              </MuiTooltip>
            </Box>

            {/* Enhanced Info Card */}
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                },
              }}
            >
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    🔍 Advanced Analytics Dashboard
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Real-time system monitoring, user behavior analysis, and AI performance metrics.
                    Track usage patterns, classification accuracy, and system health indicators.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      icon={<DataUsage />}
                      label="Real-time Data"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<Insights />}
                      label="AI Insights"
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<Security />}
                      label="Admin Only"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: theme.palette.primary.main,
                      mx: 'auto',
                      mb: 2,
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    <AnalyticsIcon sx={{ fontSize: '2.5rem' }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    System Analytics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Administrator Dashboard
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Fade>

        {/* Enhanced Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                minHeight: 70,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                px: 4,
                py: 2,
              },
              '& .Mui-selected': {
                color: theme.palette.primary.main,
                background: alpha(theme.palette.primary.main, 0.05),
              },
              '& .MuiTabs-indicator': {
                height: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              },
            }}
          >
            <Tab
              icon={<Assessment />}
              iconPosition="start"
              label="System Overview"
            />
            <Tab
              icon={<Timeline />}
              iconPosition="start"
              label="User Activity"
            />
            <Tab
              icon={<ShowChart />}
              iconPosition="start"
              label="Risk Analysis"
            />
            <Tab
              icon={<Insights />}
              iconPosition="start"
              label="Advanced Analytics"
            />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <CircularProgress size={60} thickness={4} />
            </Box>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Enhanced KPI Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                          <People sx={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                            {stats?.total_users || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Users
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={85}
                            sx={{
                              mt: 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.primary.main,
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 12px 32px ${alpha(theme.palette.secondary.main, 0.2)}`,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                          <CloudUpload sx={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                            {stats?.total_images || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Images Uploaded
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={72}
                            sx={{
                              mt: 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.secondary.main,
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.2)}`,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.success.main, width: 56, height: 56 }}>
                          <Science sx={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.main }}>
                            {stats?.total_analyses || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Analyses Completed
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={91}
                            sx={{
                              mt: 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.success.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.success.main,
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        p: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 12px 32px ${alpha(theme.palette.info.main, 0.2)}`,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.info.main, width: 56, height: 56 }}>
                          <TrendingUp sx={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                            {stats?.total_images && stats?.total_analyses
                              ? ((stats.total_analyses / stats.total_images) * 100).toFixed(1)
                              : '0'
                            }%
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Analysis Rate
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={stats?.total_images && stats?.total_analyses
                              ? (stats.total_analyses / stats.total_images) * 100
                              : 0
                            }
                            sx={{
                              mt: 1,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.info.main,
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Enhanced Charts */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            <PieChartIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Analyses by Body Part
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Distribution of analyzed lesions
                            </Typography>
                          </Box>
                        </Stack>
                        {bodyPartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                              <Pie
                                data={bodyPartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={renderCustomizedLabel}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                                animationBegin={0}
                                animationDuration={1000}
                              >
                                {bodyPartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                  borderRadius: 8,
                                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                                }}
                              />
                              <Legend
                                wrapperStyle={{
                                  paddingTop: '20px',
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1), mx: 'auto', mb: 2 }}>
                              <Info color="disabled" />
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                              No analyses found for the selected period.
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        background: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(20px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        borderRadius: 3,
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.error.main} 100%)`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                          <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                            <BarChartIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Risk Level Distribution
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Classification results overview
                            </Typography>
                          </Box>
                        </Stack>
                        {riskData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={riskData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke={alpha(theme.palette.divider, 0.3)}
                              />
                              <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                stroke={theme.palette.text.secondary}
                              />
                              <YAxis
                                tick={{ fontSize: 12 }}
                                stroke={theme.palette.text.secondary}
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                  borderRadius: 8,
                                  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                                }}
                              />
                              <Bar
                                dataKey="value"
                                fill={theme.palette.secondary.main}
                                radius={[4, 4, 0, 0]}
                                animationDuration={1000}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1), mx: 'auto', mb: 2 }}>
                              <Info color="disabled" />
                            </Avatar>
                            <Typography variant="body2" color="text.secondary">
                              No risk classification data available for the selected period.
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Controls */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                  <CalendarToday />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Activity Period
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select the time range for activity analysis
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={activityDays}
                    label="Time Range"
                    onChange={(e) => setActivityDays(Number(e.target.value))}
                    sx={{
                      borderRadius: 2,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                    <MenuItem value={90}>Last 90 days</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            {activityLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress size={60} thickness={4} />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Main Activity Chart */}
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      background: alpha(theme.palette.background.paper, 0.8),
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.success.main} 100%)`,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                          <Timeline />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Daily Activity Trends
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            User engagement over the last {activityDays} days
                          </Typography>
                        </Box>
                      </Stack>
                      {activityData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={450}>
                          <ComposedChart data={activityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={alpha(theme.palette.divider, 0.3)}
                            />
                            <XAxis
                              dataKey="date"
                              tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                              tick={{ fontSize: 12 }}
                              stroke={theme.palette.text.secondary}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              stroke={theme.palette.text.secondary}
                            />
                            <Tooltip
                              labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                              contentStyle={{
                                backgroundColor: alpha(theme.palette.background.paper, 0.95),
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                borderRadius: 8,
                                boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                              }}
                            />
                            <Legend />
                            <Area
                              type="monotone"
                              dataKey="uploads"
                              fill={alpha(theme.palette.primary.main, 0.3)}
                              stroke={theme.palette.primary.main}
                              strokeWidth={3}
                              name="Image Uploads"
                              animationDuration={1500}
                            />
                            <Line
                              type="monotone"
                              dataKey="analyses"
                              stroke={theme.palette.success.main}
                              strokeWidth={3}
                              name="Analyses Performed"
                              dot={{ fill: theme.palette.success.main, strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6, stroke: theme.palette.success.main, strokeWidth: 2 }}
                              animationDuration={1500}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.text.secondary, 0.1), mx: 'auto', mb: 2, width: 64, height: 64 }}>
                            <Info color="disabled" sx={{ fontSize: '2rem' }} />
                          </Avatar>
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Activity Data
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            No user activity data found for the last {activityDays} days.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </motion.div>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Grid container spacing={3}>
              {/* Risk Overview Cards */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ bgcolor: theme.palette.success.main, mx: 'auto', mb: 2, width: 64, height: 64 }}>
                    <CheckCircle sx={{ fontSize: '2rem' }} />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.success.main, mb: 1 }}>
                    87%
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    Benign Classifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Most analyzed lesions show benign characteristics
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, mx: 'auto', mb: 2, width: 64, height: 64 }}>
                    <Warning sx={{ fontSize: '2rem' }} />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.warning.main, mb: 1 }}>
                    8%
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    Requires Attention
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cases flagged for professional evaluation
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <Avatar sx={{ bgcolor: theme.palette.error.main, mx: 'auto', mb: 2, width: 64, height: 64 }}>
                    <Science sx={{ fontSize: '2rem' }} />
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.error.main, mb: 1 }}>
                    5%
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    High Risk Detected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Urgent medical consultation recommended
                  </Typography>
                </Card>
              </Grid>

              {/* Advanced Risk Analysis Chart */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.warning.main} 50%, ${theme.palette.success.main} 100%)`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ bgcolor: theme.palette.error.main }}>
                        <ShowChart />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Risk Assessment Distribution
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Comprehensive analysis of classification confidence levels
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Mock data for demonstration */}
                    <ResponsiveContainer width="100%" height={400}>
                      <RadialBarChart
                        cx="50%"
                        cy="50%"
                        innerRadius="20%"
                        outerRadius="90%"
                        data={[
                          { name: 'Benign', value: 87, fill: theme.palette.success.main },
                          { name: 'Suspicious', value: 8, fill: theme.palette.warning.main },
                          { name: 'Malignant', value: 5, fill: theme.palette.error.main },
                        ]}
                      >
                        <RadialBar
                          dataKey="value"
                          cornerRadius={10}
                          fill="#8884d8"
                          animationDuration={2000}
                        />
                        <Legend
                          iconSize={18}
                          wrapperStyle={{
                            top: '50%',
                            right: '10%',
                            transform: 'translate(0, -50%)',
                            lineHeight: '24px',
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            borderRadius: 8,
                            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
                          }}
                        />
                      </RadialBarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Future Features Info */}
              <Grid item xs={12}>
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    '& .MuiAlert-icon': {
                      fontSize: '1.5rem',
                    },
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    🚀 Advanced Analytics Coming Soon
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We're continuously enhancing our analytics capabilities. Future updates will include:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="body2">• Temporal risk evolution tracking</Typography>
                        <Typography variant="body2">• ABCDE criteria correlation analysis</Typography>
                        <Typography variant="body2">• Geographic distribution mapping</Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack spacing={1}>
                        <Typography variant="body2">• AI confidence level trends</Typography>
                        <Typography variant="body2">• Predictive risk modeling</Typography>
                        <Typography variant="body2">• Comparative analysis tools</Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Alert>
              </Grid>
            </Grid>
          </motion.div>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Grid container spacing={4}>
              {/* System Health Card */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: systemHealth?.health_status === 'excellent'
                        ? `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`
                        : systemHealth?.health_status === 'good'
                          ? `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${theme.palette.info.light} 100%)`
                          : `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                      🏥 System Health Monitor
                    </Typography>

                    {healthLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : systemHealth ? (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              textAlign: 'center',
                              background: alpha(theme.palette.success.main, 0.1),
                              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.success.main }}>
                              {systemHealth.health_score}%
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {systemHealth.health_status}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Overall Health Score
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {systemHealth.system_metrics.total_users}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total Users
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {systemHealth.system_metrics.recent_uploads_24h}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Uploads (24h)
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {systemHealth.performance_metrics.error_rate.toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Error Rate
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {systemHealth.performance_metrics.storage_usage_percent.toFixed(1)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Storage Used
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : (
                      <Alert severity="error">Failed to load system health data</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Metrics */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderRadius: 3,
                    height: '400px',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      📊 Daily Processing Volume
                    </Typography>

                    {performanceLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        <CircularProgress />
                      </Box>
                    ) : performanceMetrics?.daily_metrics ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={performanceMetrics.daily_metrics}>
                          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                          <XAxis
                            dataKey="_id"
                            stroke={theme.palette.text.secondary}
                            fontSize={12}
                          />
                          <YAxis stroke={theme.palette.text.secondary} fontSize={12} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: alpha(theme.palette.background.paper, 0.95),
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              borderRadius: 8,
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="analyses_count"
                            stroke={theme.palette.primary.main}
                            fill={alpha(theme.palette.primary.main, 0.3)}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <Alert severity="info">No performance data available</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* User Analytics Summary */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    background: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderRadius: 3,
                    height: '400px',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      👥 User Analytics Summary
                    </Typography>

                    {detailedLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                        <CircularProgress />
                      </Box>
                    ) : detailedAnalytics ? (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              background: alpha(theme.palette.primary.main, 0.05),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Most Active User
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {detailedAnalytics.summary.most_active_user || 'N/A'}
                            </Typography>
                          </Paper>
                        </Grid>

                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                              {detailedAnalytics.summary.total_active_users}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Active Users
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.info.main }}>
                              {detailedAnalytics.summary.avg_uploads_per_user.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Avg Uploads/User
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Geographic Coverage: {detailedAnalytics.summary.geographic_coverage} regions
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(detailedAnalytics.summary.geographic_coverage * 5, 100)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: alpha(theme.palette.info.main, 0.2),
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: theme.palette.info.main,
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    ) : (
                      <Alert severity="info">No user analytics available</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </motion.div>
        </TabPanel>
      </Box>
    </motion.div>
  );
};

export default AnalyticsPage;
